import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';

import { ContasReceberConfigService } from '../../../../shared/services/apis/financas/contas-receber-config.service';
import { ContasReceberConfigRequest } from '../../../../shared/services/requests/financas/contas-receber-config.request';
import { TipoAvisoVencimento } from '../../../../shared/services/models/financas/tipo-aviso-vencimento.model';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { Router } from '@angular/router';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { TipoAvisoVencimentoEnum } from '../../../../shared/enums/financas/tipo-aviso-vencimento.enum';

interface VariavelEmail {
  codigo: string;
  descricao: string;
}

@Component({
  selector: 'app-config-contas-receber',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatCardModule,
    HeaderComponent,
    MatDialogModule,
    MatIconModule,
    MatSelectModule,
    FooterActionComponent,
    LoadingBoxComponent,
    NgxEditorModule,
    AlertCardComponent,
    MatTooltipModule,
  ],
  templateUrl: './config-contas-receber.component.html',
  styleUrls: ['./config-contas-receber.component.css'],
})
export class ConfigContasReceberComponent implements OnInit, OnDestroy, AfterViewChecked {
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;
  public moduloEnum = ModuloEnum.Configuracoes;
  public tipoAvisoVencimentoEnum = TipoAvisoVencimentoEnum;

  public opcoesTipoAviso: TipoAvisoVencimento[];
  public editor!: Editor;
  public showVariaveisEmail = false;
  public variaveisEmail: VariavelEmail[] = [
    { codigo: '[VALOR]', descricao: 'Será substituído pelo valor da fatura. Ex: 150,00' },
    { codigo: '[DATA_VENCIMENTO]', descricao: 'Será substituído pela data de vencimento da fatura. Ex: 25/12/2026' },
    {
      codigo: '[DESCRICAO_CONTA_RECEBER]',
      descricao: 'Será substituído pela descrição da conta a receber. Ex: Fatura de serviços',
    },
    { codigo: '[NOME_CLIENTE]', descricao: 'Será substituído pelo nome do cliente. Ex: João da Silva' },
    { codigo: '[NOME_EMPRESA]', descricao: 'Será substituído pelo nome da sua empresa. Ex: ATS Informática' },
  ];
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  private focusAssunto = false;
  @ViewChild('assuntoInput') assuntoInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private configService: ContasReceberConfigService,
    private alertService: AlertService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      isBoletoAutomatico: [{ value: false, disabled: false }],
      idTipoAvisoVencimento: [{ value: TipoAvisoVencimentoEnum.NaoAvisar, disabled: false }],
      assuntoEmail: [{ value: '', disabled: true }],
      corpoEmailHtml: [{ value: '', disabled: true }],
    });
  }

  ngOnInit(): void {
    this.editor = new Editor();

    this.carregarConfiguracoes();

    this.form.get('idTipoAvisoVencimento')?.valueChanges.subscribe(tipo => {
      const assuntoControl = this.form.get('assuntoEmail');
      const corpoControl = this.form.get('corpoEmailHtml');

      if (tipo > TipoAvisoVencimentoEnum.NaoAvisar) {
        assuntoControl?.enable();
        corpoControl?.enable();

        assuntoControl?.setValidators([Validators.required]);
        corpoControl?.setValidators([Validators.required]);

        if (!corpoControl?.value) {
          corpoControl?.setValue(
            '<p>Prezado <strong>[NOME_CLIENTE]</strong>,</p><p>Lembramos que a sua fatura de <strong>[DESCRICAO_CONTA_RECEBER]</strong> no valor de R$ <strong>[VALOR]</strong> pode ser paga até o dia <strong>[DATA_VENCIMENTO]</strong>.</p><p>Cordialmente, <strong>[NOME_EMPRESA]</strong>.</p>'
          );
        }
        if (!assuntoControl?.value) {
          assuntoControl?.setValue('Aviso de Vencimento de Fatura');
        }

        this.focusAssunto = true;
      } else {
        assuntoControl?.disable();
        corpoControl?.disable();

        assuntoControl?.clearValidators();
        corpoControl?.clearValidators();
      }

      assuntoControl?.updateValueAndValidity();
      corpoControl?.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  ngAfterViewChecked(): void {
    if (this.focusAssunto && this.assuntoInput) {
      this.assuntoInput.nativeElement.focus();
      this.focusAssunto = false;
    }
  }

  carregarConfiguracoes() {
    this.isLoading = true;
    this.configService.get().subscribe({
      next: (response: any) => {
        if (response.isError) {
          this.alertService.show('Alerta', response.errorDescription, AlertType.warning);
          this.isLoading = false;
          return;
        }

        this.opcoesTipoAviso = response?.opcoesTipoAviso;
        const config = response?.contasReceberConfig;

        if (config) {
          this.form.patchValue({
            isBoletoAutomatico: config.isBoletoAutomatico,
            idTipoAvisoVencimento: config.idTipoAvisoVencimento,
            assuntoEmail: config.assuntoEmail,
            corpoEmailHtml: config.corpoEmailHtml,
          });
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar configurações', AlertType.error);
      },
    });
  }

  salvar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValues = this.form.getRawValue();

    if (formValues.idTipoAvisoVencimento > TipoAvisoVencimentoEnum.NaoAvisar && formValues.corpoEmailHtml) {
      const missingValor = !formValues.corpoEmailHtml.includes('[VALOR]');
      const missingData = !formValues.corpoEmailHtml.includes('[DATA_VENCIMENTO]');
      const missingDescricao = !formValues.corpoEmailHtml.includes('[DESCRICAO_CONTA_RECEBER]');
      const missingNomeCliente = !formValues.corpoEmailHtml.includes('[NOME_CLIENTE]');
      const missingNomeEmpresa = !formValues.corpoEmailHtml.includes('[NOME_EMPRESA]');

      if (missingValor || missingData || missingDescricao || missingNomeCliente || missingNomeEmpresa) {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
          data: {
            title: 'Aviso de Variáveis Ausentes',
            innerHtml: `<p>O corpo do e-mail não contém todos os valores de substituição dinâmicos recomendados:</p>
                        <ul>
                            <li><strong><code>[VALOR]</code></strong></li>
                            <li><strong><code>[DATA_VENCIMENTO]</code></strong></li>
                            <li><strong><code>[DESCRICAO_CONTA_RECEBER]</code></strong></li>
                            <li><strong><code>[NOME_CLIENTE]</code></strong></li>
                            <li><strong><code>[NOME_EMPRESA]</code></strong></li>
                        </ul>
                        <p>O e-mail será enviado sem as informações dinâmicas do boleto de identificação. Você tem certeza que deseja continuar e salvar mesmo assim?</p>`,
            buttonClose: 'Voltar e Corrigir',
            buttonConfirm: 'Salvar Mesmo Assim',
          },
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result.confirm) {
            this.executarSalvar(formValues);
          }
        });
        return;
      }
    }

    this.executarSalvar(formValues);
  }

  private executarSalvar(formValues: any) {
    this.isSaving = true;

    const requestData: ContasReceberConfigRequest = {
      isBoletoAutomatico: formValues.isBoletoAutomatico,
      idTipoAvisoVencimento: formValues.idTipoAvisoVencimento,
      assuntoEmail:
        formValues.idTipoAvisoVencimento > TipoAvisoVencimentoEnum.NaoAvisar ? formValues.assuntoEmail : null,
      corpoEmailHtml:
        formValues.idTipoAvisoVencimento > TipoAvisoVencimentoEnum.NaoAvisar
          ? formValues.corpoEmailHtml
          : null,
    };

    this.configService.put(requestData).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso!', response.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Configuração salva com sucesso!', AlertType.success);
        this.router.navigate(['/configuracoes/financas']);
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao conectar com a API', AlertType.error);
        this.isSaving = false;
      },
    });
  }

  voltar() {
    this.router.navigate(['/configuracoes/financas']);
  }

  public toggleVariaveisEmail(): void {
    this.showVariaveisEmail = !this.showVariaveisEmail;
  }

  public inserirVariavelEmail(codigo: string): void {
    this.editor.commands.insertText(codigo).focus().exec();
    this.form.get('corpoEmailHtml')?.markAsDirty();
  }
}
